import { createRequest } from "@/common/helpers/RequestHelpers";
import { Actions as PENSION_GROUP_ACTIONS } from "@/redux/actions/groupActions";
import { Actions as INTEGRATION_ACTIONS } from "@/redux/actions/integrationActions";
import { Actions as NOTIFICATION_ACTIONS } from "@/redux/actions/notificationActions";
import { Actions as PENSION_SCHEME_ACTIONS } from "@/redux/actions/schemeActions";
import { NotificationManager } from "react-notifications";
import { INTEGRATION_NAME, INTEGRATION_TYPE, INTEGRATION_VARIABLE } from "./config";

// import apiURL from "@/apiURL";
const Types = {
	PEOPLES_PENSIONS_EMPLOYEE_LINK: "PEOPLES_PENSIONS_EMPLOYEE_LINK",
	PEOPLES_PENSIONS_GETTING_EMPLOYEES: "PEOPLES_PENSIONS_GETTING_EMPLOYEES",
	PEOPLES_PENSIONS_OPTIONS: "PEOPLES_PENSIONS_OPTIONS",
	PEOPLES_PENSIONS_SENDING_PAY_PERIOD: "PEOPLES_PENSIONS_SENDING_PAY_PERIOD",
	PEOPLES_PENSIONS_UPDATE_PAY_PERIOD: "PEOPLES_PENSIONS_UPDATE_PAY_PERIOD",
	PEOPLES_PENSIONS_ERRORS_MODAL: "PEOPLES_PENSIONS_ERRORS_MODAL",
};

const request = createRequest({ urlPath: "/integrations/peoplesPension/" });

const setOptions = (options) => ({
	type: Types.PEOPLES_PENSIONS_OPTIONS,
	options,
});
const setEmployeeLink = (links, employees) => ({
	type: Types.PEOPLES_PENSIONS_EMPLOYEE_LINK,
	links,
	employees,
});

const updatePayPeriod = (period) => ({
	type: Types.PEOPLES_PENSIONS_UPDATE_PAY_PERIOD,
	period,
});

const gettingEmployeesLinks = (status) => ({
	type: Types.PEOPLES_PENSIONS_GETTING_EMPLOYEES,
	status,
});

const toggleErrorsModal = (payPeriodId) => ({
	type: Types.PEOPLES_PENSIONS_ERRORS_MODAL,
	payPeriodId,
});

const connectPeoplesPension = () => {
	return async (dispatch, getState) => {
		const url = window.location.href;

		await request({ method: "GET", path: "connect", query: { url } })
			.then(({ data }) => {
				if (data.url === url) dispatch(INTEGRATION_ACTIONS.integrationGetList());
				else window.location.href = data.url;
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Connecting People's Pension`, `Connect People's Pension`);
			});
	};
};

const getPeoplesPensionOrganisations = () => {
	return async (dispatch, getState) => {
		dispatch(setOptions({ loadingOrganisations: true }));

		await request({ method: "GET", path: "organisations" }).then(({ data }) => {
			dispatch(setOptions({ organisations: data.organisations }));
		});

		dispatch(setOptions({ loadingOrganisations: undefined }));
	};
};

const updatePeoplePensionConnectedOrg = ({ companyName, organisationId }) => {
	return async (dispatch, getState) => {
		const state = getState();
		const existingOrg = state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.connectionId;

		if (existingOrg === organisationId) return;

		dispatch(setOptions({ switchingOrganisation: true, groups: undefined, schemes: undefined }));

		await request({ method: "POST", path: "link-organisation", body: { companyName, organisationId } }).then(
			({ data }) => {
				dispatch(setOptions({ connectionId: organisationId, companyName }));
				NotificationManager.success(
					`Successfully Linked to ${companyName}`,
					`Linking ${INTEGRATION_NAME} Company`,
				);
			},
		);

		dispatch(setOptions({ switchingOrganisation: undefined }));
	};
};

const importPensionSchemes = () => {
	return async (dispatch, getState) => {
		const state = getState();
		const connectionId = state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.connectionId;
		if (!connectionId) return;

		dispatch(setOptions({ importingSchemes: true }));

		await request({
			method: "POST",
			path: `import-schemes/${connectionId}`,
		})
			.then(({ data }) => {
				const { schemes, groups } = data;
				dispatch(PENSION_GROUP_ACTIONS.updateGroups(groups));
				dispatch(PENSION_SCHEME_ACTIONS.updateSchemes(schemes));
				NotificationManager.success(`Successfully Imported Pension Schemes`, `Importing Pension Schemes`);
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Importing Pension Schemes`, `Importing Pension Schemes`);
			});

		dispatch(setOptions({ importingSchemes: undefined }));
	};
};

const getPeoplesPensionSchemes = () => {
	return async (dispatch, getState) => {
		const state = getState();
		if (!state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.connectionId) return;

		dispatch(PENSION_SCHEME_ACTIONS.getSchemes());

		const { schemes, groups, pensionPayScheduleOptions } =
			(await request({ method: "GET", path: "schemes" }))?.data || {};

		dispatch(setOptions({ schemes, groups, pensionPayScheduleOptions }));
	};
};

const getPeoplesPensionSettings = () => {
	return async (dispatch, getState) => {
		await request({ method: "GET", path: "settings" });
	};
};

const getPeoplePensionPayPeriods = () => {
	return (dispatch, getState) => {
		INTEGRATION_ACTIONS.integrationGetPayPeriods(INTEGRATION_NAME)
			.then(({ data }) => {
				dispatch(setOptions({ payPeriods: data.payPeriods }));
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Saving ${INTEGRATION_NAME}`, `${INTEGRATION_NAME} Settings Saved`);
			});
	};
};

const savePeoplesPensionSettings = (body) => {
	return (dispatch, getState) => {
		INTEGRATION_ACTIONS.integrationUpdateSettings(INTEGRATION_NAME, body)
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Saving ${INTEGRATION_NAME}`, `${INTEGRATION_NAME} Settings Saved`);
			});
	};
};

const sendingPayPeriod = (periodId, status) => ({
	type: Types.PEOPLES_PENSIONS_SENDING_PAY_PERIOD,
	periodId,
	status,
});

const sendPayPeriodToPeoplesPension = (periodId) => {
	return async (dispatch, getState) => {
		const state = getState();

		const sending = state.integration?.[INTEGRATION_TYPE]?.[INTEGRATION_VARIABLE]?.sendingPayroll || [];

		if (sending.includes(periodId)) return;

		dispatch(sendingPayPeriod(periodId, true));

		await request({ method: "POST", path: "payPeriod", body: { periodId } })
			.then(({ data }) => {
				switch (data.status) {
					case "success":
						NotificationManager.success(
							`Successfully Sent Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
					case "error":
						NotificationManager.error(
							`Error Sending Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
				}
				dispatch(updatePayPeriod(data));
			})
			.catch((error) => {
				let message;
				if (error.isAxiosError) {
					message = error.response?.data?.error;
				} else {
					console.error(error);
				}
				NotificationManager.error(
					message ? message : `Error Sending Pay Period to ${INTEGRATION_NAME}`,
					`Send Pay Period to ${INTEGRATION_NAME}`,
				);
			});

		dispatch(sendingPayPeriod(periodId));
	};
};

const getPayPeriodStatus = (periodId) => {
	return async (dispatch, getState) => {
		console.debug(periodId);
		await request({ method: "GET", path: `payPeriod/${periodId}` })
			.then(({ data }) => {
				console.debug(data.status);
				switch (data.status) {
					case "success":
						NotificationManager.success(
							`Successfully Sent Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
					case "error":
						NotificationManager.error(
							`Error Sending Pay Period to ${INTEGRATION_NAME}`,
							`Send Pay Period to ${INTEGRATION_NAME}`,
						);
						break;
				}
				dispatch(updatePayPeriod(data));
			})
			.catch((error) => {
				console.error(error);
			});
	};
};

const disconnectPeoplesPension = () => {
	return async (dispatch, getState) => {
		await request({ method: "DELETE", path: "revoke" })
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Disconnecting ${INTEGRATION_NAME}`, `Disconnect ${INTEGRATION_NAME}`);
			});
	};
};

const getOptOuts = (query) => {
	return async (dispatch, getState) => {
		console.debug(query);
		await request({ method: "GET", path: "opt-outs", query })
			.then(({ data }) => {
				// console.debug(data);
				if (data?.optOuts?.length > 0) NOTIFICATION_ACTIONS.getNotifications();
			})
			.catch((error) => {
				console.error(error);
			});
	};
};

const Actions = {
	connectPeoplesPension,
	disconnectPeoplesPension,
	setEmployeeLink,
	updatePeoplePensionConnectedOrg,
	gettingEmployeesLinks,
	savePeoplesPensionSettings,
	getPeoplesPensionSettings,
	getPeoplesPensionSchemes,
	getPeoplesPensionOrganisations,
	importPensionSchemes,
	sendPayPeriodToPeoplesPension,
	getPayPeriodStatus,
	toggleErrorsModal,
	getOptOuts,
};

export { Actions, Types };
